/**
 * @author        yewon@zumper.com (Yewon Lee)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Message Button Container
 */

import {
  getUser,
  selectEntity,
  selectIsMobile,
  setSendingMessage,
} from '@zcode-ui/web-zumper-global-state'
import {
  CLICK_MESSAGE,
  UNDO_MESSAGE,
  event,
  getEntityProperties,
} from '@zcode-ui/web-zumper-integrations'
import { EVENT_NAMES, PROP_NAMES, track } from '@zcode/mixpanel'
import { type AnalyticsC, Origin } from '@zcode/nr-utils'
import { API_RESPONSE } from '@zcode/nr/es/shared/actions/sendMessage/helper/handleErrorAnalyticsAndButter'
import {
  type APPLY,
  type MESSAGE,
  TOUR,
  type WAITLIST,
} from '@zcode/nr/es/shared/constants/message'
import { getRouteProps } from '@zcode/nr/es/shared/packages/history-middleware/actions'
import { hocCompose, withErrorBoundary } from '@zcode/react-utils'
import type {
  FloorplanListing,
  Listable,
  Listing,
  NormalFloorplanListingData,
  Pin,
  Property,
  RawPinInventorySearch,
  SendMessageArgs,
  State,
} from '@zcode/state-type'
import { routes } from '@zcode/zumper-routes'
import { connect } from 'react-redux'
import invariant from 'tiny-invariant'
import { sendMessage } from '../../actions/sendMessage/sendMessage.action'
import { sendMessageSuccess } from '../../actions/sendMessage/sendMessageSuccess'
import { isMessaged } from '../../inspectors/index'
import { logger } from '../../logger'
import { setLastClickedEntity } from '../../modules/currentSearch/actions/index'
import { getCommonProps } from '../../modules/mixpanel/helpers/getCommonProps'
import { getPageNameFromRouteProps } from '../../modules/mixpanel/helpers/getPageNameFromRouteProps'
import { getPreviousPage } from '../../modules/mixpanel/helpers/previousPageLocalStorage'
import { default as fetchMessagingEntity } from '../../modules/modals/thunks/messageModal/fetchMessagingEntity'
import { showMessagingModal } from '../../modules/modals/thunks/messageModal/showMessagingModal'
import {
  selectFetchingTourIntegrationForGroupId,
  selectIsFetchingAnyTourIntegration,
} from '../../modules/multifamilyTourTimePicker/selectors'
import { getTraktorPageType } from '../../modules/traktor/thunks/trackDetailEvent'
import { trackMessageImpression } from '../../modules/traktor/thunks/trackMessageImpression'
import type { Dispatch } from '../../modules/types'
import { getHasInstantTourAvailability } from '../message/MessageForm/TourOptions/getHasInstantTourAvailability'
import { default as MessageButton, type MessageButtonProps } from './MessageButton'

interface ContainerProps {
  analyticChannel:
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
  entity: Listable | Property | Pin<RawPinInventorySearch> | null | undefined
  equalCTAIntent?: typeof APPLY | typeof MESSAGE | typeof TOUR | typeof WAITLIST
  isQuestionModal?: boolean
  onlyShowFloorplanGroup?: boolean
  origin: Origin
  extraPayload?: { currentTab?: string }
  selectedFloorplan?: FloorplanListing<NormalFloorplanListingData>
  showAgentInfo?: boolean
  text?: string
  viewingId?: number
  viewingTime?: string
}

export const mapStateToProps = (state: State, ownProps: ContainerProps) => {
  return {
    isMessaged: isMessaged(state, ownProps.entity),
    isFetchingAnyTourIntegration: selectIsFetchingAnyTourIntegration(state),
    isFetchingThisTourIntegration:
      !!ownProps.entity?.groupId &&
      selectFetchingTourIntegrationForGroupId(state, ownProps.entity.groupId),
  } satisfies Partial<MessageButtonProps>
}

export const mapDispatchToProps = (dispatch: Dispatch, ownProps: ContainerProps) => {
  const {
    analyticChannel,
    isQuestionModal,
    equalCTAIntent,
    entity,
    origin,
    onlyShowFloorplanGroup,
    selectedFloorplan,
    showAgentInfo,
    viewingId,
    viewingTime,
  } = ownProps
  const ctaName = ownProps.text || 'Message'
  return {
    fetchMessagingEntity: async (
      listable: Listing | (Listable & { isBuilding: true }) | Pin<RawPinInventorySearch>
    ) => {
      const { listingId, buildingId } = listable
      if (listable.isBuilding && buildingId) {
        return await dispatch(
          fetchMessagingEntity({ buildingId, listingId: listingId || undefined })
        )
      }
      if (listingId) {
        return await dispatch(fetchMessagingEntity({ listingId }))
      }
    },
    onClick: async ({
      messageEntity,
    }: {
      messageEntity: Listable | Property | null | undefined
    }) => {
      dispatch(async (_, getState) => {
        const state = getState()
        const entity = messageEntity ?? selectEntity(state)
        invariant(entity, 'entity must not be falsy')
        const messageData = {
          analyticChannel,
          currentTab: ownProps?.extraPayload?.currentTab,
          entity,
          ...(equalCTAIntent === TOUR
            ? {
                equalCTAIntent,
                hasInstantTourAvailability: await dispatch(getHasInstantTourAvailability(entity)),
              }
            : {
                equalCTAIntent,
              }),
          ctaName,
          isQuestionModal,
          onlyShowFloorplanGroup,
          origin,
          selectedFloorplan,
          showAgentInfo,
          viewingId,
          viewingTime,
        }
        origin === Origin.LIST_ITEM && dispatch(setLastClickedEntity(entity))
        dispatch(trackMessageImpression({ entity, origin, ctaName }))
        dispatch(showMessagingModal(messageData))
      })
    },
    sendMessage: async (data: SendMessageArgs) => {
      const { analyticChannel, entity } = data
      try {
        origin === Origin.LIST_ITEM && dispatch(setLastClickedEntity(entity))
        const result = await dispatch(sendMessage(data))
        const { email, moveIn, name, phone } = data.messageData
        return dispatch(
          sendMessageSuccess({
            analyticChannel,
            currentTab: ownProps?.extraPayload?.currentTab,
            email,
            entity,
            oneTap: data.oneTap,
            isFilterAd: entity?.isFilterAd,
            ctaName,
            moveIn,
            name,
            origin,
            phone,
            result,
          })
        )
      } catch (e) {
        // We don't need to log when the error is due to the user having an existing account
        if (
          e &&
          typeof e === 'object' &&
          'data' in e &&
          e.data &&
          typeof e.data === 'object' &&
          'reason' in e.data &&
          e.data.reason !== API_RESPONSE.OTHER_USER
        ) {
          logger.error('Error in one tap send message', e)
        }
      }
    },
    trackMessageClick: () => {
      dispatch((_, getState) => {
        const state = getState()
        const isMobile = selectIsMobile(state)
        const routeProps = dispatch(getRouteProps({ routes }))
        const pageName = getPageNameFromRouteProps({ routeProps, isMobile })
        track({
          event: EVENT_NAMES.CLICK_MESSAGE,
          payload: {
            [PROP_NAMES.PREVIOUS_PAGE]: getPreviousPage(),
            [PROP_NAMES.CURRENT_TAB]: ownProps?.extraPayload?.currentTab,
            ...getCommonProps({
              state,
              entity,
              pageName,
              origin,
              event: EVENT_NAMES.CLICK_MESSAGE,
            }),
          },
        })
        const user = getUser(state)
        const entityProperties = getEntityProperties(entity ?? {}, state)
        const props = {
          origin,
          ...entityProperties,
          ctaName,
          pageType: getTraktorPageType(entity ?? {}, routeProps.match, state),
          ...ownProps?.extraPayload,
        }
        dispatch(event({ name: CLICK_MESSAGE, user, props }))
      })
    },
    trackUndoClick: () => {
      dispatch((_, getState) => {
        const state = getState()
        const isMobile = selectIsMobile(state)
        const routeProps = dispatch(getRouteProps({ routes }))
        const pageName = getPageNameFromRouteProps({ routeProps, isMobile })

        track({
          event: EVENT_NAMES.UNDO_MESSAGE,
          payload: {
            [PROP_NAMES.PREVIOUS_PAGE]: getPreviousPage(),
            ...getCommonProps({ state, entity, pageName, origin }),
          },
        })
        const user = getUser(state)
        const entityProperties = getEntityProperties(entity ?? {}, state)
        const props = {
          origin,
          ...entityProperties,
          ctaName,
          pageType: getTraktorPageType(entity ?? {}, routeProps.match, state),
        }
        dispatch(event({ name: UNDO_MESSAGE, user, props }))
      })
    },
    setSendingMessage: (isSending: boolean) => {
      dispatch(setSendingMessage(isSending))
    },
  } satisfies Partial<MessageButtonProps>
}

export const MessageButtonContainer = hocCompose(
  connect(mapStateToProps, mapDispatchToProps, null, { forwardRef: true }),
  withErrorBoundary({ displayName: 'MessageButton' }),
  MessageButton
)
